/**
 * THE CHECKOUT IS SPLIT INTO ARMS THAT CAN BE TOLD APART.
 *
 * 2026-10-09: `plain` (the checkout as it shipped) against `total` (the price in the button, the reassurance as plain
 * text, no guided focus, the name marked required). 2026-10-12, the owner: "I want all these new changes of today to
 * be under AB testing" — so the second arm became `lean`: everything `total` was, plus the day's changes (the payment
 * step that is the payment, the basket in the bar beside the button with its drawer, the delivery stated as a fact,
 * add leading the product bar, the folded "good to know", no top totals bar). And the same evening: "do it under ab
 * testing with 70% going to it" — `steps`, the checkout that asks one thing per screen (who → where → pay), built on
 * `lean`, takes 70 of every 100 visitors; `lean` and `plain` take 15 each.
 *
 * WHAT A VISITOR GETS: the hash of their visitor key, modulo 100, laid against the weights — the same key always lands
 * in the same place. Adding the 70 arm re-drew the buckets once, on the night it shipped (2026-10-12): the two-way
 * split HAD been live since a3e406c04 (deployed 10-10), so a returning visitor could change arm that night. It had
 * carried fourteen arm-tagged events in two days — measured on the table by the deploy session, not assumed — so the
 * comparison it interrupted had nothing to lose. (I had written here that it never reached production; it had.)
 *
 * The arm rides on `checkout_env.exp` (Checkout/index.js) and the product page reads the same function, so one
 * visitor sees one checkout from the product bar to the payment.
 *
 * FORCING AN ARM — for the owner's own walk and for the suites: `?checkout_arm=steps|lean|plain` on any store URL
 * is remembered for the device (localStorage `stylon:checkout:arm`); `window.__stylonCheckoutArm` wins over
 * everything (setupTests.js sets it to `lean`, so a suite that does not say otherwise exercises the lean checkout; a
 * suite about the steps pins `steps`; a suite that mocks this module pins what it likes).
 *
 * AND FOR A WHOLE BUILD: `REACT_APP_CHECKOUT_ARM=plain|lean|steps` at build time pins every visitor of that bundle
 * to one arm, below the query string and the remembered choice and above the hash. It exists because the Playwright
 * suite's store is such a build: on the night the 70/15/15 split shipped (2026-10-13) seven checkout specs written for
 * the one-screen checkout met the steps checkout seventy times in a hundred and the train did not ship — a spec
 * about delivery tiers was deciding by hash which checkout it walked. The suite's bundle is pinned to `plain`
 * (e2e/playwright.config.ts), and a spec about another arm says so with `?checkout_arm=` on its first URL. A
 * production build leaves it unset and the experiment runs. Note on 'process.env' here: CRA inlines it at build.
 */
export const CHECKOUT_ARMS = ['plain', 'lean', 'steps'];
/** Of every 100 visitors: the owner's share for each arm (2026-10-12 evening). The control is what is left. */
export const CHECKOUT_ARM_SHARES = { steps: 70, lean: 15, plain: 15 };
export const CHECKOUT_ARM_STORAGE_KEY = 'stylon:checkout:arm';
export const CHECKOUT_ARM_QUERY = 'checkout_arm';

function hashOf(key) {
  let h = 0x811c9dc5;
  const s = String(key || '');
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** An arm forced on this device (the query string, remembered) or on this page (the test hook), else null. */
export function forcedCheckoutArm() {
  if (typeof window === 'undefined') return null;
  try {
    const hook = window.__stylonCheckoutArm;
    if (hook && CHECKOUT_ARMS.includes(hook)) return hook;
  } catch (_) { /* no window */ }
  try {
    const q = window.location && window.location.search
      ? new URLSearchParams(window.location.search).get(CHECKOUT_ARM_QUERY)
      : null;
    if (q && CHECKOUT_ARMS.includes(q)) {
      try { window.localStorage.setItem(CHECKOUT_ARM_STORAGE_KEY, q); } catch (_) { /* private mode */ }
      return q;
    }
    const stored = window.localStorage ? window.localStorage.getItem(CHECKOUT_ARM_STORAGE_KEY) : null;
    if (stored && CHECKOUT_ARMS.includes(stored)) return stored;
  } catch (_) { /* storage blocked */ }
  const built = buildPinnedCheckoutArm();
  if (built) return built;
  return null;
}

/** The arm this BUILD was pinned to (`REACT_APP_CHECKOUT_ARM`), else null.
 *
 *  Written as the bare `process.env.REACT_APP_CHECKOUT_ARM` ON PURPOSE: CRA replaces exactly that expression with a
 *  string at build time, and the browser has no `process` at all — a `typeof process !== 'undefined'` guard around
 *  it compiled to `typeof process !== 'undefined' ? 'plain' : null`, which is null in every browser and 'plain' in
 *  jest, so the unit tests were green while the Playwright suite's pin was dead (2026-10-13, found by the suite). */
export function buildPinnedCheckoutArm() {
  const built = process.env.REACT_APP_CHECKOUT_ARM;
  return built && CHECKOUT_ARMS.includes(built) ? built : null;
}

export function checkoutArm(visitorKey) {
  const forced = forcedCheckoutArm();
  if (forced) return forced;
  if (!visitorKey || typeof visitorKey !== 'string') return CHECKOUT_ARMS[0];
  const slot = hashOf(visitorKey) % 100;
  if (slot < CHECKOUT_ARM_SHARES.steps) return 'steps';
  if (slot < CHECKOUT_ARM_SHARES.steps + CHECKOUT_ARM_SHARES.lean) return 'lean';
  return 'plain';
}

/** The owner's 2026-10-12 checkout — `lean`, and `steps` which is built on it. Everything the first arm does not do
 *  is behind this. */
export function isLeanCheckout(arm) {
  return arm === 'lean' || arm === 'steps';
}

/** The checkout that asks one thing per screen: who → where → pay. */
export function isStepsCheckout(arm) {
  return arm === 'steps';
}
